/**
 * Permission utility functions for the CDC Mail System
 * Handles permission checking and UI authorization
 */

import store from '@/store'

// Permission mapping for UI actions
export const PERMISSIONS = {
  // Settings
  SETTINGS_VIEW: 'admin.settings.index',
  SETTINGS_EDIT: 'admin.settings.update',
  
  // Users
  USERS_VIEW: 'admin.users.index',
  USERS_CREATE: 'admin.users.store',
  USERS_EDIT: 'admin.users.update',
  
  // Roles
  ROLES_VIEW: 'admin.roles.index',
  ROLES_CREATE: 'admin.roles.store',
  ROLES_EDIT: 'admin.roles.update',
  ROLES_PERMISSIONS: 'admin.roles.permissions',
  
  // Departments
  DEPARTMENTS_VIEW: 'admin.departments.index',
  DEPARTMENTS_CREATE: 'admin.departments.store',
  DEPARTMENTS_EDIT: 'admin.departments.update',
  DEPARTMENTS_DELETE: 'admin.departments.destroy',
  DEPARTMENTS_SHOW: 'admin.departments.show',
  
  // Department Jobs
  DEPT_JOBS_VIEW: 'admin.department-jobs.index',
  DEPT_JOBS_CREATE: 'admin.department-jobs.store',
  DEPT_JOBS_EDIT: 'admin.department-jobs.update',
  DEPT_JOBS_DELETE: 'admin.department-jobs.destroy',
  DEPT_JOBS_SHOW: 'admin.department-jobs.show',
  
  // Employee Titles
  EMPLOYEE_TITLES_VIEW: 'admin.employee-titles.index',
  EMPLOYEE_TITLES_CREATE: 'admin.employee-titles.store',
  EMPLOYEE_TITLES_EDIT: 'admin.employee-titles.update',
  EMPLOYEE_TITLES_DELETE: 'admin.employee-titles.destroy',
  EMPLOYEE_TITLES_SHOW: 'admin.employee-titles.show',
  
  // Marginalizations (Notes)
  NOTES_VIEW: 'admin.marginalizations.index',
  NOTES_CREATE: 'admin.marginalizations.store',
  NOTES_EDIT: 'admin.marginalizations.update',
  NOTES_DELETE: 'admin.marginalizations.destroy',
  NOTES_SHOW: 'admin.marginalizations.show',
  
  // User Folders
  USER_FOLDERS_VIEW: 'admin.user-folders.index',
  USER_FOLDERS_CREATE: 'admin.user-folders.store',
  USER_FOLDERS_EDIT: 'admin.user-folders.update',
  USER_FOLDERS_DELETE: 'admin.user-folders.destroy',
  USER_FOLDERS_SHOW: 'admin.user-folders.show',
  
  // File Types
  FILE_TYPES_VIEW: 'admin.file-types.index',
  FILE_TYPES_CREATE: 'admin.file-types.store',
  FILE_TYPES_EDIT: 'admin.file-types.update',
  FILE_TYPES_DELETE: 'admin.file-types.destroy',
  FILE_TYPES_SHOW: 'admin.file-types.show',
  
  // Mail Priorities
  MAIL_PRIORITIES_VIEW: 'admin.mail-priorities.index',
  MAIL_PRIORITIES_CREATE: 'admin.mail-priorities.store',
  MAIL_PRIORITIES_EDIT: 'admin.mail-priorities.update',
  MAIL_PRIORITIES_DELETE: 'admin.mail-priorities.destroy',
  MAIL_PRIORITIES_SHOW: 'admin.mail-priorities.show',
  
  // Activity Logs
  ACTIVITY_LOGS_VIEW: 'admin.activity-logs.index',
  
  // Login Logs
  LOGIN_LOGS_VIEW: 'admin.login-logs-users.index'
}

// Global permission cache
let permissionIdToNameMap = null

/**
 * Load and cache permission mapping from API
 */
async function loadPermissionMapping() {
  if (permissionIdToNameMap) return permissionIdToNameMap
  
  try {
    const roleService = await import('@/services/roleService')
    const response = await roleService.default.getPermissions()
    
    permissionIdToNameMap = {}
    response.data.forEach(perm => {
      permissionIdToNameMap[perm.id] = perm.name
    })
    
    return permissionIdToNameMap
  } catch (error) {
    console.error('Failed to load permission mapping:', error)
    return {}
  }
}

/**
 * Convert permission ID to name
 * @param {number|string} permissionId - Permission ID or name
 * @returns {string} Permission name
 */
function resolvePermissionName(permissionId) {
  // If it's already a string (permission name), return as is
  if (typeof permissionId === 'string') {
    return permissionId
  }
  
  // If it's a number (permission ID), convert to name
  if (typeof permissionId === 'number' && permissionIdToNameMap) {
    return permissionIdToNameMap[permissionId] || permissionId.toString()
  }
  
  return permissionId ? permissionId.toString() : ''
}

/**
 * Check if user has a specific permission
 * @param {string} permission - Permission string to check
 * @returns {boolean} - True if user has permission
 */
export function hasPermission(permission) {
  // During logout, return false to prevent DOM errors
  if (store.state.isLoggingOut) {
    return false
  }

  const userPermissions = store.state.permissions || []
  
  // Check direct permission name match
  if (userPermissions.includes(permission)) {
    return true
  }
  
  // If permission mapping is loaded, check converted IDs
  if (permissionIdToNameMap) {
    const resolvedPermissions = userPermissions.map(resolvePermissionName)
    return resolvedPermissions.includes(permission)
  }
  
  return false
}

/**
 * Check if user has any of the provided permissions
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} - True if user has any of the permissions
 */
export function hasAnyPermission(permissions) {
  // During logout, return false to prevent DOM errors
  if (store.state.isLoggingOut) {
    return false
  }

  const userPermissions = store.state.permissions || []
  
  // First check direct matches
  if (permissions.some(permission => userPermissions.includes(permission))) {
    return true
  }
  
  // If permission mapping is loaded, check converted IDs
  if (permissionIdToNameMap) {
    const resolvedPermissions = userPermissions.map(resolvePermissionName)
    return permissions.some(permission => resolvedPermissions.includes(permission))
  }
  
  return false
}

/**
 * Check if user has all of the provided permissions
 * @param {string[]} permissions - Array of permissions to check
 * @returns {boolean} - True if user has all permissions
 */
export function hasAllPermissions(permissions) {
  const userPermissions = store.state.permissions || []
  
  // First check direct matches
  const directMatches = permissions.filter(permission => userPermissions.includes(permission))
  if (directMatches.length === permissions.length) {
    return true
  }
  
  // If permission mapping is loaded, check converted IDs
  if (permissionIdToNameMap) {
    const resolvedPermissions = userPermissions.map(resolvePermissionName)
    return permissions.every(permission => resolvedPermissions.includes(permission))
  }
  
  return false
}

/**
 * Get CRUD permissions for a resource
 * @param {string} resource - Resource name (e.g., 'departments', 'users')
 * @returns {object} - Object with CRUD permission checks
 */
export function getCrudPermissions(resource) {
  return {
    canView: hasPermission(`admin.${resource}.index`),
    canCreate: hasPermission(`admin.${resource}.store`),
    canEdit: hasPermission(`admin.${resource}.update`),
    canDelete: hasPermission(`admin.${resource}.destroy`),
    canShow: hasPermission(`admin.${resource}.show`)
  }
}

/**
 * Check if user can access a route based on permissions
 * @param {string} routeName - Route name
 * @returns {boolean} - True if user can access route
 */
export function canAccessRoute(routeName) {
  const routePermissions = {
    'users': PERMISSIONS.USERS_VIEW,
    'departments': PERMISSIONS.DEPARTMENTS_VIEW,
    'settings': PERMISSIONS.SETTINGS_VIEW,
    'notes': PERMISSIONS.NOTES_VIEW,
    'roles': PERMISSIONS.ROLES_VIEW
  }
  
  const requiredPermission = routePermissions[routeName]
  return requiredPermission ? hasPermission(requiredPermission) : true
}

/**
 * Filter menu items based on user permissions
 * @param {array} menuItems - Array of menu items
 * @returns {array} - Filtered menu items
 */
export function filterMenuByPermissions(menuItems) {
  return menuItems.filter(item => {
    if (!item.permission) return true
    return hasPermission(item.permission)
  })
}

/**
 * Get user's permission summary for debugging
 * @returns {object} - Permission summary
 */
export function getPermissionSummary() {
  const userPermissions = store.state.permissions || []
  const resources = ['users', 'departments', 'roles', 'settings', 'marginalizations', 'mail-priorities']
  
  const summary = {}
  resources.forEach(resource => {
    summary[resource] = getCrudPermissions(resource)
  })
  
  // Convert IDs to names for debugging
  const resolvedPermissions = permissionIdToNameMap 
    ? userPermissions.map(resolvePermissionName)
    : userPermissions
  
  return {
    totalPermissions: userPermissions.length,
    permissions: userPermissions,
    resolvedPermissions,
    permissionMapping: permissionIdToNameMap,
    resourceAccess: summary
  }
}

/**
 * Permission directive helper for Vue components
 * Usage: v-permission="'admin.users.index'"
 */
export function checkPermission(permission) {
  return hasPermission(permission)
}

/**
 * Helper for showing/hiding UI elements based on permissions
 * @param {string|array} permissions - Permission(s) to check
 * @param {string} mode - 'any' or 'all' (default: 'any')
 * @returns {boolean} - True if should show element
 */
export function showIfAllowed(permissions, mode = 'any') {
  if (typeof permissions === 'string') {
    return hasPermission(permissions)
  }
  
  if (Array.isArray(permissions)) {
    return mode === 'all' 
      ? hasAllPermissions(permissions)
      : hasAnyPermission(permissions)
  }
  
  return true
}

/**
 * Debug function to log user's current permissions
 * Call this from browser console: window.debugPermissions()
 */
export function debugPermissions() {
  const userPermissions = store.state.permissions || []
  const user = store.state.user
  const roles = store.state.roles || []
  
  console.group('🔐 User Permissions Debug')
  //console.log('👤 User:', user?.name || 'Not logged in')
  //console.log('🎭 Roles:', roles)
  //console.log('📋 Total Permissions:', userPermissions.length)
  //console.log('🔑 Raw Permissions:', userPermissions)
  
  // Show resolved permissions if mapping is available
  if (permissionIdToNameMap) {
    //const resolvedPermissions = userPermissions.map(resolvePermissionName)
    //console.log('✅ Resolved Permissions:', resolvedPermissions)
  }
  
  // Check key sections
  const keyPermissions = {
    'Departments': hasPermission(PERMISSIONS.DEPARTMENTS_VIEW),
    'Users': hasPermission(PERMISSIONS.USERS_VIEW),
    'Settings': hasPermission(PERMISSIONS.SETTINGS_VIEW),
    'Notes': hasPermission(PERMISSIONS.NOTES_VIEW)
  }
  //console.log('🔍 Key Section Access:', keyPermissions)
  console.groupEnd()
  
  return {
    user: user?.name,
    roles,
    totalPermissions: userPermissions.length,
    permissions: userPermissions,
    resolvedPermissions: permissionIdToNameMap ? userPermissions.map(resolvePermissionName) : null,
    permissionMapping: permissionIdToNameMap,
    sectionAccess: keyPermissions
  }
}

/**
 * Check if user has access to a specific module
 * @param {string} moduleName - Module name to check
 * @returns {boolean} - True if user has access to module
 */
export function hasModule(moduleName) {
  // During logout, return false to prevent DOM errors
  if (store.state.isLoggingOut) {
    return false
  }
  
  return store.getters.hasModule(moduleName)
}

/**
 * Check if user has a specific action within a module
 * @param {string} moduleName - Module name
 * @param {string} actionName - Action name
 * @returns {boolean} - True if user has the action
 */
export function hasAction(moduleName, actionName) {
  // During logout, return false to prevent DOM errors
  if (store.state.isLoggingOut) {
    return false
  }
  
  return store.getters.hasAction(moduleName, actionName)
}

/**
 * Check if user has any of the specified actions within a module
 * @param {string} moduleName - Module name
 * @param {string[]} actions - Array of action names
 * @returns {boolean} - True if user has any of the actions
 */
export function hasAnyAction(moduleName, actions) {
  // During logout, return false to prevent DOM errors
  if (store.state.isLoggingOut) {
    return false
  }
  
  return store.getters.hasAnyAction(moduleName, actions)
}

/**
 * Get all available actions for a module
 * @param {string} moduleName - Module name
 * @returns {string[]} - Array of available actions
 */
export function getModuleActions(moduleName) {
  return store.getters.getModuleActions(moduleName)
}

/**
 * Get all available modules
 * @returns {string[]} - Array of module names
 */
export function getAvailableModules() {
  return store.getters.availableModules
}

/**
 * Get CRUD permissions for a module using the new system
 * @param {string} moduleName - Module name (e.g., 'departments', 'users')
 * @returns {object} - Object with CRUD permission checks
 */
export function getModuleCrudPermissions(moduleName) {
  return {
    canView: hasAction(moduleName, 'index'),
    canCreate: hasAction(moduleName, 'store'),
    canEdit: hasAction(moduleName, 'update'),
    canDelete: hasAction(moduleName, 'destroy'),
    canShow: hasAction(moduleName, 'show')
  }
}

/**
 * Check if user can access a route based on module permissions
 * @param {string} routeName - Route name
 * @returns {boolean} - True if user can access route
 */
export function canAccessModuleRoute(routeName) {
  const routeModules = {
    'users': 'users',
    'departments': 'departments',
    'settings': 'settings',
    'notes': 'marginalizations',
    'roles': 'roles',
    'templates': 'mail-templates',
    'inbox': 'mails',
    'sent': 'mails',
    'drafts': 'mails',
    'archive': 'mails'
  }
  
  const moduleName = routeModules[routeName]
  if (!moduleName) return true // Allow access if no specific module required
  
  return hasModule(moduleName)
}

/**
 * Filter menu items based on module permissions
 * @param {array} menuItems - Array of menu items with module property
 * @returns {array} - Filtered menu items
 */
export function filterMenuByModules(menuItems) {
  return menuItems.filter(item => {
    if (!item.module) return true
    return hasModule(item.module)
  })
}

/**
 * Get user's module permission summary for debugging
 * @returns {object} - Module permission summary
 */
export function getModulePermissionSummary() {
  const modules = store.getters.modules
  const availableModules = Object.keys(modules)
  
  const summary = {}
  availableModules.forEach(moduleName => {
    summary[moduleName] = {
      actions: modules[moduleName].actions,
      crud: getModuleCrudPermissions(moduleName)
    }
  })
  
  return {
    totalModules: availableModules.length,
    availableModules,
    modules: summary
  }
}

// Initialize permission mapping when module loads
loadPermissionMapping()

// Make debug function available globally
if (typeof window !== 'undefined') {
  window.debugPermissions = debugPermissions
}

export default {
  PERMISSIONS,
  hasPermission,
  hasAnyPermission,
  hasAllPermissions,
  getCrudPermissions,
  canAccessRoute,
  filterMenuByPermissions,
  getPermissionSummary,
  checkPermission,
  showIfAllowed,
  debugPermissions,
  loadPermissionMapping,
  hasModule,
  hasAction,
  hasAnyAction,
  getModuleActions,
  getAvailableModules,
  getModuleCrudPermissions,
  canAccessModuleRoute,
  filterMenuByModules,
  getModulePermissionSummary
} 